import { useState, useCallback } from 'react'
import {
  User, Lock, CreditCard, Bell, Globe, Shield, Key,
  Smartphone, AlertTriangle, Copy, Eye, EyeOff, RefreshCw,
  Check, X, Loader2, Trash2, Plus, CheckCircle2, Mail, Zap
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import toast from 'react-hot-toast'

interface ApiKey {
  id: string
  name: string
  key: string
  created: string
  lastUsed: string
}

export function Settings() {
  const { user, logout } = useAuthStore()
  const [activeTab, setActiveTab] = useState('general')

  // Form states
  const [companyName, setCompanyName] = useState('Acme Corporation Inc.')
  const [adminEmail, setAdminEmail] = useState('admin@acme-corp.com')
  const [workspaceDomain, setWorkspaceDomain] = useState('acme-corp')
  const [isSaving, setIsSaving] = useState(false)

  // Security toggles
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true)
  const [ipRestriction, setIpRestriction] = useState(false)
  const [sessionTimeout, setSessionTimeout] = useState(true)
  const [auditLogs, setAuditLogs] = useState(true)

  // API Keys
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([
    { id: '1', name: 'Production API', key: 'nxs_prod_8x7f2k9j3m4n5p6q', created: '15 Gen 2024', lastUsed: '2 ore fa' },
    { id: '2', name: 'Development', key: 'nxs_dev_1a2b3c4d5e6f7g8h', created: '20 Gen 2024', lastUsed: '5 giorni fa' },
  ])
  const [showKey, setShowKey] = useState<string | null>(null)
  const [newKeyName, setNewKeyName] = useState('')
  const [showNewKeyModal, setShowNewKeyModal] = useState(false)
  const [isGeneratingKey, setIsGeneratingKey] = useState(false)

  // Notifications
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [transcriptReady, setTranscriptReady] = useState(true)
  const [weeklyReport, setWeeklyReport] = useState(true)
  const [securityAlerts, setSecurityAlerts] = useState(true)

  // Language
  const [language, setLanguage] = useState('it')
  const [timezone, setTimezone] = useState('Europe/Rome')
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY')

  // Danger zone
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const handleSaveProfile = useCallback(() => {
    setIsSaving(true)
    toast.loading('Salvataggio in corso...', { id: 'save-profile' })
    setTimeout(() => {
      setIsSaving(false)
      toast.success('Impostazioni salvate con successo!', { id: 'save-profile' })
    }, 1500)
  }, [])

  const handleCopyKey = useCallback((key: string) => {
    navigator.clipboard.writeText(key)
    toast.success('API Key copiata negli appunti!')
  }, [])

  const handleGenerateKey = useCallback(() => {
    if (!newKeyName.trim()) {
      toast.error('Inserisci un nome per la chiave')
      return
    }

    setIsGeneratingKey(true)
    toast.loading('Generazione chiave in corso...', { id: 'gen-key' })

    setTimeout(() => {
      const newKey: ApiKey = {
        id: Date.now().toString(),
        name: newKeyName,
        key: `nxs_${newKeyName.toLowerCase().replace(/\s/g, '_')}_${Math.random().toString(36).substring(2, 18)}`,
        created: 'Adesso',
        lastUsed: 'Mai',
      }

      setApiKeys(prev => [...prev, newKey])
      setNewKeyName('')
      setShowNewKeyModal(false)
      setIsGeneratingKey(false)
      toast.success('Nuova API Key generata!', { id: 'gen-key' })
    }, 1500)
  }, [newKeyName])

  const handleRevokeKey = useCallback((id: string, name: string) => {
    setApiKeys(prev => prev.filter(k => k.id !== id))
    toast.success(`Chiave "${name}" revocata.`)
  }, [])

  const handleDeleteWorkspace = useCallback(() => {
    if (deleteConfirmText !== 'ELIMINA') {
      toast.error('Digita ELIMINA per confermare')
      return
    }

    toast.loading('Eliminazione workspace in corso...', { id: 'delete-ws' })
    setTimeout(() => {
      toast.success('Workspace eliminato. Reindirizzamento...', { id: 'delete-ws' })
      setTimeout(() => {
        logout()
      }, 1500)
    }, 2000)
  }, [deleteConfirmText, logout])

  const Toggle = ({ enabled, onChange }: { enabled: boolean; onChange: (val: boolean) => void }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
        enabled ? 'bg-emerald-500' : 'bg-slate-200'
      }`}
    >
      <span className={`${enabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} />
    </button>
  )

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Configurazione Workspace</h1>
        <p className="text-slate-500 mt-1">Gestisci le preferenze globali, la sicurezza e le integrazioni.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar Navigazione */}
        <div className="lg:col-span-3 space-y-1">
          <nav className="space-y-1">
            {[
              { id: 'general', icon: User, label: 'Profilo Aziendale' },
              { id: 'security', icon: Shield, label: 'Sicurezza & SSO' },
              { id: 'api', icon: Key, label: 'API Keys' },
              { id: 'billing', icon: CreditCard, label: 'Fatturazione' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <tab.icon className="h-4 w-4" /> {tab.label}
              </button>
            ))}
          </nav>

          <div className="pt-6 mt-6 border-t border-slate-200">
            <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Preferenze</p>
            <button
              onClick={() => setActiveTab('language')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'language' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Globe className="h-4 w-4" /> Lingua & Regione
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'notifications' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Bell className="h-4 w-4" /> Notifiche
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-9 space-y-6">

          {/* TAB: General / Profile */}
          {activeTab === 'general' && (
            <>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                  <h2 className="font-semibold text-slate-800">Dettagli Organizzazione</h2>
                  <span className="text-xs font-mono text-slate-500">ID: ORG-8823-XJ</span>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome Azienda</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email Amministratore</label>
                    <input
                      type="email"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all"
                    />
                  </div>
                  <div className="col-span-full">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Dominio Workspace</label>
                    <div className="flex mt-2">
                      <span className="inline-flex items-center px-4 py-2.5 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-slate-500 text-sm">nexscribe.io/</span>
                      <input
                        type="text"
                        value={workspaceDomain}
                        onChange={(e) => setWorkspaceDomain(e.target.value)}
                        className="flex-1 bg-white border border-slate-200 rounded-r-lg px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all"
                      />
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Salva Modifiche
                  </button>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="border border-red-200 rounded-xl overflow-hidden bg-red-50/30">
                <div className="p-6 flex items-start justify-between">
                  <div className="flex gap-4">
                    <div className="p-2 bg-red-100 text-red-600 rounded-lg h-fit"><AlertTriangle className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-bold text-red-900">Danger Zone</h3>
                      <p className="text-sm text-red-700 mt-1">Queste azioni sono irreversibili. Procedere con cautela.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="text-red-600 border border-red-200 bg-white hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Elimina Workspace
                  </button>
                </div>
              </div>
            </>
          )}

          {/* TAB: Security */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-slate-500" />
                  Policy di Sicurezza
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                <div className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex gap-4">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg h-fit"><Smartphone className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Autenticazione a Due Fattori (2FA)</h3>
                      <p className="text-sm text-slate-500 mt-1">Richiedi a tutti i membri di usare un'app authenticator.</p>
                    </div>
                  </div>
                  <Toggle enabled={twoFactorEnabled} onChange={(val) => {
                    setTwoFactorEnabled(val)
                    toast.success(val ? '2FA abilitato per tutti i membri' : '2FA disabilitato')
                  }} />
                </div>

                <div className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex gap-4">
                    <div className="p-2 bg-slate-100 text-slate-600 rounded-lg h-fit"><Globe className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Restrizione IP</h3>
                      <p className="text-sm text-slate-500 mt-1">Consenti l'accesso solo da indirizzi IP aziendali approvati.</p>
                    </div>
                  </div>
                  <Toggle enabled={ipRestriction} onChange={(val) => {
                    setIpRestriction(val)
                    toast.success(val ? 'Restrizione IP attivata' : 'Restrizione IP disattivata')
                  }} />
                </div>

                <div className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex gap-4">
                    <div className="p-2 bg-amber-50 text-amber-600 rounded-lg h-fit"><Lock className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Timeout Sessione Automatico</h3>
                      <p className="text-sm text-slate-500 mt-1">Disconnetti automaticamente dopo 30 minuti di inattivita.</p>
                    </div>
                  </div>
                  <Toggle enabled={sessionTimeout} onChange={(val) => {
                    setSessionTimeout(val)
                    toast.success(val ? 'Timeout sessione attivato' : 'Timeout sessione disattivato')
                  }} />
                </div>

                <div className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex gap-4">
                    <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg h-fit"><Shield className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Audit Logs</h3>
                      <p className="text-sm text-slate-500 mt-1">Registra tutte le azioni degli utenti per compliance.</p>
                    </div>
                  </div>
                  <Toggle enabled={auditLogs} onChange={(val) => {
                    setAuditLogs(val)
                    toast.success(val ? 'Audit logs attivato' : 'Audit logs disattivato')
                  }} />
                </div>
              </div>
            </div>
          )}

          {/* TAB: API Keys */}
          {activeTab === 'api' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                <div>
                  <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Key className="h-4 w-4 text-slate-500" />
                    API Keys
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Gestisci le chiavi per l'accesso programmatico.</p>
                </div>
                <button
                  onClick={() => setShowNewKeyModal(true)}
                  className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" /> Nuova Chiave
                </button>
              </div>

              {apiKeys.length === 0 ? (
                <div className="p-12 text-center">
                  <Key className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Nessuna API Key</h3>
                  <p className="text-sm text-slate-500 mb-4">Genera la prima chiave per iniziare.</p>
                  <button
                    onClick={() => setShowNewKeyModal(true)}
                    className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" /> Genera Chiave
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {apiKeys.map((apiKey) => (
                    <div key={apiKey.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 text-slate-600 rounded-lg">
                          <Key className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900">{apiKey.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono text-slate-600">
                              {showKey === apiKey.id ? apiKey.key : `${apiKey.key.substring(0, 12)}${'•'.repeat(16)}`}
                            </code>
                            <button
                              onClick={() => setShowKey(showKey === apiKey.id ? null : apiKey.id)}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              {showKey === apiKey.id ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">Creata: {apiKey.created} • Ultimo uso: {apiKey.lastUsed}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCopyKey(apiKey.key)}
                          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Copia"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleRevokeKey(apiKey.id, apiKey.name)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Revoca"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: Billing */}
          {activeTab === 'billing' && (
            <>
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 blur-[80px] rounded-full pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-white/10 rounded-lg">
                      <Zap className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-bold">{user?.planName || `Piano ${user?.plan?.charAt(0).toUpperCase()}${user?.plan?.slice(1) || 'Free'}`}</h3>
                      <p className="text-xs text-slate-400">
                        {user?.planStartDate
                          ? `Attivo dal ${new Date(user.planStartDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}`
                          : 'Piano attivo'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-6 mt-6">
                    <div>
                      <p className="text-2xl font-bold">{user?.minutesUsed || 0}</p>
                      <p className="text-xs text-slate-400">Minuti usati</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{user?.minutesLimit || 60}</p>
                      <p className="text-xs text-slate-400">Minuti totali</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{user?.voiceIdsLimit === -1 ? 'Illimitati' : user?.voiceIdsLimit || 3}</p>
                      <p className="text-xs text-slate-400">Voice IDs</p>
                    </div>
                  </div>

                  {/* Usage Progress Bars */}
                  <div className="mt-6 space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-400">Utilizzo minuti</span>
                        <span className="text-white font-semibold">
                          {Math.round((user?.minutesUsed || 0) / (user?.minutesLimit || 60) * 100)}%
                        </span>
                      </div>
                      <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            ((user?.minutesUsed || 0) / (user?.minutesLimit || 60)) > 0.9 ? 'bg-red-500' :
                            ((user?.minutesUsed || 0) / (user?.minutesLimit || 60)) > 0.7 ? 'bg-amber-500' :
                            'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(100, ((user?.minutesUsed || 0) / (user?.minutesLimit || 60)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-400">Voice IDs utilizzati</span>
                        <span className="text-white font-semibold">
                          {user?.voiceIdsUsed || 0} / {user?.voiceIdsLimit || 3}
                        </span>
                      </div>
                      <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, ((user?.voiceIdsUsed || 0) / (user?.voiceIdsLimit || 3)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {user?.planPrice && (
                    <div className="mt-6 pt-4 border-t border-white/20">
                      <p className="text-xs text-slate-400">Costo mensile</p>
                      <p className="text-2xl font-bold">{user.planPrice}€<span className="text-sm font-normal text-slate-400">/mese</span></p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                  <h2 className="font-semibold text-slate-800">Dettagli Account</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</p>
                      <p className="font-medium text-slate-900">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Account creato</p>
                      <p className="font-medium text-slate-900">
                        {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('it-IT', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        }) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {user?.company && (
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Azienda</p>
                        <p className="font-medium text-slate-900">{user.company}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg h-fit">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900">Hai bisogno di più risorse?</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Per modificare il tuo piano o aumentare i limiti, contatta l'amministratore.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* TAB: Language */}
          {activeTab === 'language' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-slate-500" />
                  Lingua & Regione
                </h2>
              </div>
              <div className="p-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Lingua Interfaccia</label>
                  <select
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value)
                      toast.success('Lingua aggiornata!')
                    }}
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all cursor-pointer"
                  >
                    <option value="it" className="text-slate-900">Italiano</option>
                    <option value="en" className="text-slate-900">English</option>
                    <option value="de" className="text-slate-900">Deutsch</option>
                    <option value="fr" className="text-slate-900">Français</option>
                    <option value="es" className="text-slate-900">Español</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Fuso Orario</label>
                  <select
                    value={timezone}
                    onChange={(e) => {
                      setTimezone(e.target.value)
                      toast.success('Fuso orario aggiornato!')
                    }}
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all cursor-pointer"
                  >
                    <option value="Europe/Rome" className="text-slate-900">Europe/Rome (UTC+1)</option>
                    <option value="Europe/London" className="text-slate-900">Europe/London (UTC+0)</option>
                    <option value="America/New_York" className="text-slate-900">America/New_York (UTC-5)</option>
                    <option value="Asia/Tokyo" className="text-slate-900">Asia/Tokyo (UTC+9)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Formato Data</label>
                  <select
                    value={dateFormat}
                    onChange={(e) => {
                      setDateFormat(e.target.value)
                      toast.success('Formato data aggiornato!')
                    }}
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all cursor-pointer"
                  >
                    <option value="DD/MM/YYYY" className="text-slate-900">DD/MM/YYYY (31/12/2024)</option>
                    <option value="MM/DD/YYYY" className="text-slate-900">MM/DD/YYYY (12/31/2024)</option>
                    <option value="YYYY-MM-DD" className="text-slate-900">YYYY-MM-DD (2024-12-31)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* TAB: Notifications */}
          {activeTab === 'notifications' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Bell className="h-4 w-4 text-slate-500" />
                  Preferenze Notifiche
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                <div className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex gap-4">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg h-fit"><Mail className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Notifiche Email</h3>
                      <p className="text-sm text-slate-500 mt-1">Ricevi aggiornamenti importanti via email.</p>
                    </div>
                  </div>
                  <Toggle enabled={emailNotifications} onChange={(val) => {
                    setEmailNotifications(val)
                    toast.success(val ? 'Notifiche email attivate' : 'Notifiche email disattivate')
                  }} />
                </div>

                <div className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex gap-4">
                    <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg h-fit"><CheckCircle2 className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Trascrizione Pronta</h3>
                      <p className="text-sm text-slate-500 mt-1">Notifica quando una trascrizione è completata.</p>
                    </div>
                  </div>
                  <Toggle enabled={transcriptReady} onChange={(val) => {
                    setTranscriptReady(val)
                    toast.success(val ? 'Notifica trascrizione attivata' : 'Notifica trascrizione disattivata')
                  }} />
                </div>

                <div className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex gap-4">
                    <div className="p-2 bg-violet-50 text-violet-600 rounded-lg h-fit"><CreditCard className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Report Settimanale</h3>
                      <p className="text-sm text-slate-500 mt-1">Ricevi un riepilogo settimanale delle attivita.</p>
                    </div>
                  </div>
                  <Toggle enabled={weeklyReport} onChange={(val) => {
                    setWeeklyReport(val)
                    toast.success(val ? 'Report settimanale attivato' : 'Report settimanale disattivato')
                  }} />
                </div>

                <div className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex gap-4">
                    <div className="p-2 bg-red-50 text-red-600 rounded-lg h-fit"><AlertTriangle className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Avvisi di Sicurezza</h3>
                      <p className="text-sm text-slate-500 mt-1">Notifiche per accessi sospetti o violazioni.</p>
                    </div>
                  </div>
                  <Toggle enabled={securityAlerts} onChange={(val) => {
                    setSecurityAlerts(val)
                    toast.success(val ? 'Avvisi sicurezza attivati' : 'Avvisi sicurezza disattivati')
                  }} />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Delete Workspace Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <div className="p-2 bg-red-100 text-red-600 rounded-lg">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-lg text-slate-900">Elimina Workspace</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                Questa azione e irreversibile. Tutti i dati, registrazioni, trascrizioni e profili vocali verranno eliminati permanentemente.
              </p>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Digita ELIMINA per confermare
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="ELIMINA"
                  className="w-full bg-white border border-red-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeleteConfirmText('')
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteWorkspace}
                disabled={deleteConfirmText !== 'ELIMINA'}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-4 w-4" /> Elimina Workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New API Key Modal */}
      {showNewKeyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-900">Nuova API Key</h3>
              <button
                onClick={() => setShowNewKeyModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome Chiave *</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Es. Production API"
                  className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all"
                />
              </div>
              <p className="text-xs text-slate-500 mt-3">
                La chiave sara visibile una sola volta. Salvala in un luogo sicuro.
              </p>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setShowNewKeyModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleGenerateKey}
                disabled={isGeneratingKey}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isGeneratingKey ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Genera Chiave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
